import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens';

const palette = (mode: 'light' | 'dark') => {
  const t = tokens[mode];
  return {
    primary: { main: tokens.brand.primary },
    secondary: { main: tokens.brand.secondary },
    success: { main: tokens.feedback.success },
    warning: { main: tokens.feedback.warning },
    error: { main: tokens.feedback.error },
    info: { main: tokens.feedback.info },
    background: { default: t.background, paper: t.surface },
    text: { primary: t.text, secondary: t.textMuted },
    divider: t.border,
  };
};

/**
 * The app theme. Colours come from tokens.ts; this file sets typography and default component looks.
 * CSS variables + colorSchemes give light/dark switching without re-rendering (see useColorScheme).
 * `direction` flips the layout for right-to-left languages such as Arabic.
 */
export const createAppTheme = (direction: 'ltr' | 'rtl') =>
  createTheme({
    direction,
    cssVariables: { colorSchemeSelector: 'class' },
    colorSchemes: {
      light: { palette: palette('light') },
      dark: { palette: palette('dark') },
    },
    spacing: tokens.spacing,
    shape: { borderRadius: tokens.radius },
    typography: {
      fontFamily: tokens.font.family,
      h1: { fontSize: '2rem', fontWeight: tokens.font.headingWeight },
      h2: { fontSize: '1.6rem', fontWeight: tokens.font.headingWeight },
      h3: { fontSize: '1.3rem', fontWeight: tokens.font.headingWeight },
      h4: { fontSize: '1.1rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            border: `1px solid ${theme.vars.palette.divider}`,
          }),
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: ({ theme }) => ({
            border: 'none',
            borderBottom: `1px solid ${theme.vars.palette.divider}`,
            backgroundColor: theme.vars.palette.background.paper,
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: { paper: { borderTop: 'none', borderBottom: 'none', borderLeft: 'none' } },
      },
      MuiMenu: {
        styleOverrides: { paper: ({ theme }) => ({ boxShadow: theme.shadows[4] }) },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({ fontWeight: 600, color: theme.vars.palette.text.secondary }),
        },
      },
      MuiTextField: {
        defaultProps: { fullWidth: true },
      },
      MuiDialog: {
        defaultProps: { fullWidth: true, maxWidth: 'sm' },
      },
      MuiListItemButton: {
        styleOverrides: { root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius }) },
      },
    },
  });
